import * as runtime from '../runtime';
import type {
  CreateMediaRequest,
  CreateMediaResponse,
  ErrorResource,
  MediaRecord,
  UpdateMediaListingRequest,
  UpdateMediaListingResponse,
  UpdateMediaRequest,
  UploadImagesResponse,
} from '../models';

export interface CreateMediaOperationRequest {
  createMediaRequest: CreateMediaRequest;
}

export interface DeleteMediaRequest {
  mediaId: string;
}

export interface GetMediaByListingIdRequest {
  listingId: number;
}

export interface UpdateMediaOperationRequest {
  mediaId: string;
  updateMediaRequest: UpdateMediaRequest;
}

export interface UpdateMediaListingMappingRequest {
  uploadSessionId: UUID;
  updateMediaListingRequest: UpdateMediaListingRequest;
}

export interface UploadImagesToVercelBlobRequest {
  images: Array<Blob>;
  metadata?: string;
}

export interface UploadImagesToVercelBlob0Request {
  images: Array<Blob>;
  metadata?: string;
}

/**
 *
 */
export class MediaApi extends runtime.BaseAPI {
  async createMediaRaw(
    requestParameters: CreateMediaOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<CreateMediaResponse>> {
    if (
      requestParameters.createMediaRequest === null ||
      requestParameters.createMediaRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'createMediaRequest',
        'Required parameter requestParameters.createMediaRequest was null or undefined when calling createMedia.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/media`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.createMediaRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Creates media records associated with a listing. Each image includes metadata and is linked to a Vercel Blob storage location.
   * Create media records for a listing
   */
  async createMedia(
    requestParameters: CreateMediaOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<CreateMediaResponse> {
    const response = await this.createMediaRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async deleteMediaRaw(
    requestParameters: DeleteMediaRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<void>> {
    if (
      requestParameters.mediaId === null ||
      requestParameters.mediaId === undefined
    ) {
      throw new runtime.RequiredError(
        'mediaId',
        'Required parameter requestParameters.mediaId was null or undefined when calling deleteMedia.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/media/{mediaId}`.replace(
          `{${'mediaId'}}`,
          encodeURIComponent(String(requestParameters.mediaId)),
        ),
        method: 'DELETE',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.VoidApiResponse(response);
  }

  /**
   * Deletes an existing media record from the system.
   * Delete a media record
   */
  async deleteMedia(
    requestParameters: DeleteMediaRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<void> {
    await this.deleteMediaRaw(requestParameters, initOverrides);
  }

  async getMediaByListingIdRaw(
    requestParameters: GetMediaByListingIdRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<Array<MediaRecord>>> {
    if (
      requestParameters.listingId === null ||
      requestParameters.listingId === undefined
    ) {
      throw new runtime.RequiredError(
        'listingId',
        'Required parameter requestParameters.listingId was null or undefined when calling getMediaByListingId.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/media/listing/{listingId}`.replace(
          `{${'listingId'}}`,
          encodeURIComponent(String(requestParameters.listingId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Retrieves all media records associated with a specific listing ID.
   * Get all media for a listing
   */
  async getMediaByListingId(
    requestParameters: GetMediaByListingIdRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<Array<MediaRecord>> {
    const response = await this.getMediaByListingIdRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async updateMediaRaw(
    requestParameters: UpdateMediaOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<MediaRecord>> {
    if (
      requestParameters.mediaId === null ||
      requestParameters.mediaId === undefined
    ) {
      throw new runtime.RequiredError(
        'mediaId',
        'Required parameter requestParameters.mediaId was null or undefined when calling updateMedia.',
      );
    }

    if (
      requestParameters.updateMediaRequest === null ||
      requestParameters.updateMediaRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'updateMediaRequest',
        'Required parameter requestParameters.updateMediaRequest was null or undefined when calling updateMedia.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/media/{mediaId}`.replace(
          `{${'mediaId'}}`,
          encodeURIComponent(String(requestParameters.mediaId)),
        ),
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.updateMediaRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Updates an existing media record with new metadata or properties.
   * Update a media record
   */
  async updateMedia(
    requestParameters: UpdateMediaOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<MediaRecord> {
    const response = await this.updateMediaRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async updateMediaListingMappingRaw(
    requestParameters: UpdateMediaListingMappingRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<UpdateMediaListingResponse>> {
    if (
      requestParameters.uploadSessionId === null ||
      requestParameters.uploadSessionId === undefined
    ) {
      throw new runtime.RequiredError(
        'uploadSessionId',
        'Required parameter requestParameters.uploadSessionId was null or undefined when calling updateMediaListingMapping.',
      );
    }

    if (
      requestParameters.updateMediaListingRequest === null ||
      requestParameters.updateMediaListingRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'updateMediaListingRequest',
        'Required parameter requestParameters.updateMediaListingRequest was null or undefined when calling updateMediaListingMapping.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/media/session/{uploadSessionId}`.replace(
          `{${'uploadSessionId'}}`,
          encodeURIComponent(String(requestParameters.uploadSessionId)),
        ),
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.updateMediaListingRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Updates media records from a given upload session by linking them to the specified listing ID.
   * Assign uploaded media to a specific listing
   */
  async updateMediaListingMapping(
    requestParameters: UpdateMediaListingMappingRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<UpdateMediaListingResponse> {
    const response = await this.updateMediaListingMappingRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async uploadImagesToVercelBlobRaw(
    requestParameters: UploadImagesToVercelBlobRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<UploadImagesResponse>> {
    if (
      requestParameters.images === null ||
      requestParameters.images === undefined
    ) {
      throw new runtime.RequiredError(
        'images',
        'Required parameter requestParameters.images was null or undefined when calling uploadImagesToVercelBlob.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const consumes: runtime.Consume[] = [
      { contentType: 'multipart/form-data' },
    ];
    // @ts-ignore: canConsumeForm may be unused
    const canConsumeForm = runtime.canConsumeForm(consumes);

    let formParams: { append(param: string, value: any): any };
    let useForm = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    useForm = canConsumeForm;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }

    if (requestParameters.images) {
      requestParameters.images.forEach(element => {
        formParams.append('images', element as any);
      });
    }

    if (requestParameters.metadata !== undefined) {
      formParams.append('metadata', requestParameters.metadata as any);
    }

    const response = await this.request(
      {
        path: `/media/upload-images`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: formParams,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Accepts multipart/form-data containing image files and optional metadata. Each uploaded image is stored in Vercel Blob storage and returns a URL along with metadata such as size, content type, and order index.
   * Upload one or more images to Vercel Blob storage.
   */
  async uploadImagesToVercelBlob(
    requestParameters: UploadImagesToVercelBlobRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<UploadImagesResponse> {
    const response = await this.uploadImagesToVercelBlobRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async uploadImagesToVercelBlob_1Raw(
    requestParameters: UploadImagesToVercelBlob0Request,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<UploadImagesResponse>> {
    if (
      requestParameters.images === null ||
      requestParameters.images === undefined
    ) {
      throw new runtime.RequiredError(
        'images',
        'Required parameter requestParameters.images was null or undefined when calling uploadImagesToVercelBlob_1.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const consumes: runtime.Consume[] = [
      { contentType: 'multipart/form-data' },
    ];
    // @ts-ignore: canConsumeForm may be unused
    const canConsumeForm = runtime.canConsumeForm(consumes);

    let formParams: { append(param: string, value: any): any };
    let useForm = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    useForm = canConsumeForm;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }

    if (requestParameters.images) {
      requestParameters.images.forEach(element => {
        formParams.append('images', element as any);
      });
    }

    if (requestParameters.metadata !== undefined) {
      formParams.append('metadata', requestParameters.metadata as any);
    }

    const response = await this.request(
      {
        path: `/media/upload-images`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: formParams,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Accepts multipart/form-data containing image files and optional metadata. Each uploaded image is stored in Vercel Blob storage and returns a URL along with metadata such as size, content type, and order index.
   * Upload one or more images to Vercel Blob storage.
   */
  async uploadImagesToVercelBlob_1(
    requestParameters: UploadImagesToVercelBlob0Request,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<UploadImagesResponse> {
    const response = await this.uploadImagesToVercelBlob_1Raw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
